import React, { useState, useRef, useEffect } from 'react';

type ButtonTheme = 'primary' | 'secondary' | 'success' | 'danger' | 'neutral' | 'white';
type IconPosition = 'leading' | 'trailing';
type ButtonSize = 'xs' | 'small' | 'medium';


type ButtonProps = {
    theme?: ButtonTheme;
    onClick?: () => void;
    disabled?: boolean;
    isLoading?: boolean;
    minHeight?: string;
    minWidth?: string;
    fullHeight?: boolean;
    fullWidth?: boolean;
    children?: React.ReactNode;
    icon?: React.ReactElement;
    iconPosition?: IconPosition;
    className?: string;
    size?: ButtonSize;
    type?: 'button' | 'submit' | 'reset';
};

const baseClasses = "rounded-full transition-all duration-300 flex items-center justify-center focus-visible:outline-2 focus-visible:outline-offset-2 ";

const styleClasses = {
    primary: {
        filled: "shadow-sm bg-primary-500 text-white disabled:bg-gray-400 border border-primary-500 disabled:border-gray-400",
        outlined: "shadow-sm border border-primary-500 bg-white text-primary-500 disabled:border-gray-500 disabled:text-gray-500",
        flat: "shadow-sm text-primary-500 bg-primary-50 disabled:text-gray-500",
        text: "hover:shadow-none text-primary-500 hover:text-primary-400",
        ai: "text-white bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600",
    },
    secondary: {
        filled: "shadow-sm bg-secondary-500 text-white disabled:bg-gray-400 border border-secondary-500 disabled:border-gray-400",
        outlined: "shadow-sm border border-secondary-500 bg-white text-secondary-500 disabled:border-gray-500 disabled:text-gray-500",
        flat: "shadow-sm text-secondary-500 bg-secondary-50 disabled:text-gray-500",
        text: "shadow-none hover:shadow-none text-secondary-500 hover:text-secondary-400",
        ai: "text-white bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600",
    },
    danger: {
        filled: "shadow-sm bg-red-500 text-white disabled:bg-gray-400 border border-red-500 disabled:border-gray-400",
        outlined: "shadow-sm border border-red-500 bg-white text-red-500 disabled:border-gray-500 disabled:text-gray-500",
        flat: "shadow-sm text-red-500 bg-red-50 disabled:text-gray-500",
        text: "shadow-none hover:shadow-none text-red-500 hover:text-red-400",
        ai: "text-white bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600",
    },
    success: {
        filled: "shadow-sm bg-green-500 text-white disabled:bg-gray-400 border border-green-500 disabled:border-gray-400",
        outlined: "shadow-sm border border-green-500 bg-white text-green-500 disabled:border-gray-500 disabled:text-gray-500",
        flat: "shadow-sm text-green-500 bg-green-50 disabled:text-gray-500",
        text: "shadow-none hover:shadow-none text-green-500 hover:text-green-400",
        ai: "text-white bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600",
    },
    neutral: {
        filled: "shadow-sm bg-black text-white hover:bg-black/80 disabled:bg-gray-400 border border-black disabled:border-gray-400",
        outlined: "shadow-sm border border-black bg-white text-black disabled:border-gray-500 disabled:text-gray-500",
        flat: "shadow-sm text-black bg-black disabled:text-gray-500",
        text: "hover:shadow-none text-black hover:text-gray-400",
        ai: "",
    },
    white: {
        filled: "shadow-sm bg-white text-black disabled:bg-gray-400 border border-white disabled:border-gray-400",
        outlined: "shadow-sm border border-white text-white disabled:border-gray-500 disabled:text-gray-500",
        flat: "shadow-sm text-white bg-white disabled:text-gray-500",
        text: "hover:shadow-none text-white hover:text-primary-100",
        ai: "",
    },
};

const LoadingSpinner: React.FC<{ size: ButtonSize, variant: string }> = ({ size, variant }) => {
    return (
        <svg aria-hidden="true"
            className={`${size === 'small' ? 'w-4 h-4' : 'w-5 h-5'}  animate-spin fill-gray-700 ${variant === 'text'
                ? 'text-gray-200' : 'text-white'}`}
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
        </svg>
    );
};

const RippleEffect: React.FC<{ x: number; y: number }> = ({ x, y }) => {
    return (
        <span
            className="absolute block rounded-full bg-white/30 animate-ripple"
            style={{
                left: x,
                top: y,
                transform: 'translate(-50%, -50%)',
            }}
        />
    );
};

const ButtonBase: React.FC<ButtonProps & { variant: keyof typeof styleClasses.primary }> = ({
    theme = 'primary',
    variant,
    onClick,
    disabled = false,
    isLoading = false,
    minHeight,
    minWidth,
    fullHeight = false,
    fullWidth = false,
    icon,
    iconPosition = 'trailing',
    className = '',
    children,
    size = 'medium',
    type = 'button',
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    const [isTouched, setIsTouched] = useState(false);
    const [ripples, setRipples] = useState<{ x: number; y: number; id: number }[]>([]);
    const touchTimerRef = useRef<NodeJS.Timeout>();
    const buttonRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        // Detect if device is touch-enabled
        setIsTouchDevice('ontouchstart' in window || navigator.maxTouchPoints > 0);
    }, []);

    const handleTouchStart = () => {
        if (!isTouchDevice) return;

        // Start a timer to show the hint
        touchTimerRef.current = setTimeout(() => {
            setIsTouched(true);
        }, 300); // 300ms delay to differentiate from a tap
    };

    const handleTouchEnd = () => {
        if (!isTouchDevice) return;

        // Clear the timer if it hasn't fired yet
        if (touchTimerRef.current) {
            clearTimeout(touchTimerRef.current);
        }
    };

    const handleTouchCancel = () => {
        if (!isTouchDevice) return;

        // Clear the timer and reset touch state
        if (touchTimerRef.current) {
            clearTimeout(touchTimerRef.current);
        }
        setIsTouched(false);
    };

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        // If it's a touch device and we're showing the hint, don't trigger the button click
        if (isTouchDevice && isTouched) {
            e.preventDefault();
            setIsTouched(false);
            return;
        }

        // Add ripple effect
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const y = e.clientY - rect.top;
            const rippleId = Date.now();

            setRipples(prev => [...prev, { x, y, id: rippleId }]);

            // Remove ripple after animation
            setTimeout(() => {
                setRipples(prev => prev.filter(ripple => ripple.id !== rippleId));
            }, 600);
        }

        onClick?.();
    };

    const fullClasses = `${fullHeight ? 'h-full' : ''} ${fullWidth ? 'w-full' : ''}`;
    const customStyles = {
        minHeight,
        minWidth
    };

    let iconWithSize;
    if (icon) {
        iconWithSize = React.cloneElement(icon, {
            className: `${icon.props.className || (children && iconPosition === 'trailing') ? 'ml-2' : (children && iconPosition === 'leading') ? 'mr-2' : ''}`.trim(),
        });
    }

    return (
        <div className={`relative inline-block group ${fullWidth ? 'w-full' : ''}`}>
            <button
                ref={buttonRef}
                className={`
                    ${baseClasses} 
                    ${styleClasses[theme][variant]} 
                    ${fullClasses} 
                    ${className} 
                    ${!disabled && !isLoading && 'hover:shadow-md'}
                    ${size === 'small'
                        ? 'py-1 px-2 text-sm font-medium'
                        : size === 'xs'
                            ? 'py-1 px-2 text-xs font-medium'
                            : 'py-1 px-3 text-md font-medium'}
                    relative overflow-hidden
                    `}
                onClick={handleClick}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchCancel}
                disabled={disabled || isLoading}
                style={customStyles}
                type={type}
            >
                {ripples.map(ripple => (
                    <RippleEffect key={ripple.id} x={ripple.x} y={ripple.y} />
                ))}
                {isLoading
                    ?
                    <LoadingSpinner size={size} variant={variant} />
                    : iconWithSize
                        ? <>
                            {iconPosition === 'leading' && iconWithSize}
                            {children}
                            {iconPosition === 'trailing' && iconWithSize}
                        </>
                        : children}
            </button>
        </div>
    );
};

export const FilledButton: React.FC<ButtonProps> = (props) => <ButtonBase {...props} variant="filled" />;
export const OutlinedButton: React.FC<ButtonProps> = (props) => <ButtonBase {...props} variant="outlined" />;
export const FlatButton: React.FC<ButtonProps> = (props) => <ButtonBase {...props} variant="flat" />;
export const TextButton: React.FC<ButtonProps> = (props) => <ButtonBase {...props} variant="text" />;
export const AIButton: React.FC<ButtonProps> = (props) => <ButtonBase {...props} variant="ai" />;